/**
 * @module $Name$
 * @extends React.Component
 * Container Component for App
 */
/* eslint react/no-multi-comp: 0*/

import React, {Suspense, useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {Redirect, Route, Switch, useLocation, withRouter} from 'react-router-dom';
import Highcharts from 'highcharts';
import LoaderContainer from './LoaderContainer';
import {LoadingIndicator} from '@ey/canvascoreservices';
import Main from './Main/Main';
import env from '../util/env';
import {getAppInsights} from './Common/AppInsight/TelemetryService';
import {hot} from 'react-hot-loader';
import theme from '@ey/styled-theme';
import {useSelector, useDispatch} from 'react-redux';
import {
	ReactiveFrame,
	toggleFrameModal,
	frameEvents,
	useFrameService,
	frameCommunicationEvents
} from '@ey/canvascoreservices';
import Utility from '../util/utilityfunctions';
import {labels} from '../util/utils';
import {frameSubEvent, serviceURL, TaskCategory} from '../util/uiconstants.js';
import {useEngagementId, useIsSuperModalOpen} from '../util/customhooks';
import {raiseJSValidationError} from '../actions/erroractions';
import {getQuickLinks} from '../actions/Milestone/QuickLinks/quicklinksactions';
import queryString from 'query-string';
import {CookieConsentModal, CookieSettings, adobeAnalytics} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import {pageViewArgs} from './Common/AdobeAnalytics/actionHelpers.js';
import {pageNames, siteSections} from './Common/AdobeAnalytics/constants.js';
import ToastMessageContainer from './Common/Toast/ToastMessageContainer';
const searchParams = new URLSearchParams(location.search);
const engagementId = searchParams.get('engagementId') || searchParams.get('engagementid');

const COOKIE_SETTINGS_PATH = `/cookiesettings?engagementId=${engagementId}`;
const adobeAnalyticsEnabled = envConfig.adobeAnalytics?.enabled;

//import {Switch, Route, withRouter} from 'react-router-dom';

//Highchart initialization. this is to avoid workaround windows.Highcharts = Highcharts
(function(H) {
	H.seriesTypes.pie.prototype.drawEmpty = function() {
		var centerX,
			centerY,
			start = this.startAngleRad,
			end = this.endAngleRad,
			options = this.options;
		// Draw auxiliary graph if there're no visible points.
		if (this.total === 0) {
			centerX = this.center[0];
			centerY = this.center[1];
			if (!this.graph) {
				this.graph = this.chart.renderer
					.arc(centerX, centerY, this.center[1] / 2, 0, start, end)
					.addClass('highcharts-empty-series')
					.add(this.group);
			}
			this.graph.attr({
				d: H.SVGRenderer.prototype.symbols.arc(centerX, centerY, this.center[2] / 2, 0, {
					start: start,
					end: end,
					innerR: this.center[3] / 2
				})
			});
			if (!this.chart.styledMode) {
				this.graph.attr({
					'stroke-width': options.borderWidth,
					fill: options.fillColor || 'none',
					stroke: options.color || theme.color['grey300']
				});
			}
		} else if (this.graph) {
			// Destroy the graph object.
			this.graph = this.graph.destroy();
		}
	};
})(Highcharts);

const TaskLayout = React.lazy(() => import('./Task/TaskLayout/ParentTaskLayout')); //(props) => Async.load(props, import('./Task/TaskLayout/ParentTaskLayout'));
const RNHParent = React.lazy(() => import('./ReviewNoteHub/RNHParent')); // (props) => Async.load(props, import('./ReviewNoteHub/RNHParent.js'));
const ArcTimelineLayout = React.lazy(() => import('./ArcTimeline/ArcTimelineLayout')); // (props) => Async.load(props, import('./ArcTimeline/ArcTimelineLayout'));
const WizardsDryDock = React.lazy(() => import('./Main/DryDock/WizardsDryDock')); // (props) => Async.load(props, import('./Main/DryDock/WizardsDryDock'));
const MavericksDryDock = React.lazy(() => import('./Main/DryDock/mavericks/MavericksDryDock')); // (props) => Async.load(props, import('./Main/DryDock/MavericksDryDock'));
const InvinciblesDryDock = React.lazy(() => import('./Main/DryDock/Invincibles/InvinciblesDryDock')); // (props)=>Async.load(props, import('./Main/DryDock/Invincibles/InvinciblesDryDock'));
const KnightridersDryDock = React.lazy(() => import('./Main/DryDock/knightriders/KnightridersDryDock.js'));
const SprinterDryDock = React.lazy(() => import('./Main/DryDock/Sprinters/DevelopersWorkSpace/SprinterDryDock')); // (props) => Async.load(props, import('./Main/DryDock/Sprinters/DeveloporsWorkSpace/SprinterDryDock'));
const GilasDryDock = React.lazy(() => import('./Main/DryDock/GilasDryDock'));
const GroupInstructions = React.lazy(() => import('./GroupInstrunctions/GroupInstructionsWrapper'));
const smartScreens = React.lazy(() => import('./Main/SmartScreens/SmartScreenContainer')); // (props) => Async.load(props, import('./Main/SmartScreens/SmartScreenContainer'));
const DashboardV2 = React.lazy(() => import('./DashboardV2/Dashboard')); //(props) => Async.load(props, import('./DashboardV2/Dashboard'));
const ManageTeamLayout = React.lazy(() => import('./ManageTeam/ManageTeamLayout/ManageTeamLayout')); // (props) => Async.load(props, import('./ManageTeam/ManageTeamLayout/ManageTeamLayout'));
const CopyHubLayout = React.lazy(() => import('./CopyHub/CopyHubLayout/copyhublayout')); // (props) => Async.load(props, import('./CopyHub/CopyHubLayout/copyhublayout'));
const DocumentListLayout = React.lazy(() => import('./Main/Documents/DocumentsLayout')); // (props) => Async.load(props, import('./Main/Documents/DocumentsLayout'));
const DocumentOpen = React.lazy(() => import('./Main/Documents/DocumentOpenParent')); // (props) => Async.load(props, import('./Main/Documents/DocumentOpenParent'));
const HelixProjectsLayout = React.lazy(() => import('./HelixProjectsV2/Layout'));
const HelixProjectsLayoutConnection = React.lazy(() => import('./HelixProjectsV2Connections/LayoutConnection'));
// const ContentUpdate = (props) => Async.load(props, import('./Main/ContentUpdate/ContentUpdates/ContentUpdatesContainer'));
const ContentUpdateLayout = React.lazy(() => import('./Main/ContentUpdate/ContentUpdateLayout/ContentUpdateLayout.js')); // (props) => Async.load(props, import('./Main/ContentUpdate/ContentUpdateLayout/ContentUpdateLayout.js'));
const CreatePackageWizard = React.lazy(() => import('./CopyHub/CreatePackageWizard/createpackagewizard.js')); // (props) => Async.load(props, import('./CopyHub/CreatePackageWizard/createpackagewizard.js'));
const Collaboration = React.lazy(() => import('../components/Collaboration/Layout')); // (props) => Async.load(props, import('../components/Collaboration/Layout'));
const CollaborationV2 = React.lazy(() => import('./CollaborationV2/CollaborationLayout')); // (props) => Async.load(props, import('./CollaborationV2/CollaborationLayout'));
const ActivityFeedLayout = React.lazy(() => import('../components/ActivityFeed/ActivityFeedLayout')); // (props) => Async.load(props, import('../components/ActivityFeed/ActivityFeedLayout'));
const GroupSummary = React.lazy(() => import('../components/GroupSummary')); // (props) => Async.load(props, import('../components/GroupSummary'));
const ReceiveInstructions = React.lazy(() => import('../components/ReceiveInstructions')); // const ReceiveInstructions = (props) => Async.load(props, import('../components/ReceiveInstructions'));
const Reports = React.lazy(() => import('./Reporting/ReportDashboard'));
const ReportingGrid = React.lazy(() => import('./ReportingGrid/ReportingGrid'));
const SystemDiagnosticDocuments = React.lazy(() => import('./HelixProjects/HelixImportList'));
const VoiceNoteDryDock = React.lazy(() => import('./Main/DryDock/VoiceNoteDryDock')); // (props) => Async.load(props, import('./Main/DryDock/WizardsDryDock'));
const EyqDrydock = React.lazy(() => import('./Main/DryDock/EyqDrydock')); // (props) => Async.load(props, import('./Main/DryDock/WizardsDryDock'));
const VideoDrydock = React.lazy(() => import('./Main/DryDock/VideoDryDock')); // (props) => Async.load(props, import('./Main/DryDock/WizardsDryDock'));
const RoyalChallengersDryDock = React.lazy(() => import('./Main/DryDock/RoyalChallengers/RoyalChallengersDryDock'));
const AdjustmentLayout = React.lazy(() => import('./TopUpModule/index'));
const SunrisersDrydock = React.lazy(() => import('./Main/DryDock/Sunrisers/SunrisersDrydock'));
const DryDock = React.lazy(() => import('./Main/DryDock/DryDock'));
const buildstep = React.lazy(() => import('./BuildStepsV2/SuperModalTrigger'));
const LocalLandingPageWrapper = React.lazy(() => import('./LocalLandingPage/LocalLandingPageWrapper'));
const FinancialStatements = React.lazy(() =>
	import('./FinancialStatements/FinancialStatements/FinancialStatementsWrapper.js')
);
const LeadSheetView = React.lazy(() => import('./FinancialStatements/LeadSheetView/LeadSheetWrapper'));
const ClassView = React.lazy(() => import('./FinancialStatements/ClassView/ClassView'));
const SidequestDrydock = React.lazy(() => import('./Main/DryDock/Seahawks/SidequestDrydock'));

let routes = [
	{
		path: '/tasks/:categoryId?/:subcategoryId?/:taskId?',
		component: adobeAnalytics?.TrackPageView(TaskLayout, pageViewArgs(pageNames.taskLayout, siteSections.taskLayout))
	},
	{
		path: '/drydock/sunrisers',
		component: SunrisersDrydock
	},
	{
		path: '/drydock',
		component: DryDock
	},
	{
		path: '/drydock/voicenotes',
		component: VoiceNoteDryDock
	},
	{
		path: '/drydock/EyqDrydock',
		component: EyqDrydock
	},
	{
		path: '/drydock/video',
		component: VideoDrydock
	},
	{
		path: '/drydock/wizards',
		component: WizardsDryDock
	},
	{
		path: '/drydock/Gilas',
		component: GilasDryDock
	},
	{
		path: '/drydock/invincibles',
		component: InvinciblesDryDock
	},
	{
		path: '/drydock/knightriders',
		component: KnightridersDryDock
	},
	{
		path: '/drydock/sprinters',
		component: SprinterDryDock
	},
	{
		path: '/drydock/mavericks',
		component: MavericksDryDock
	},
	{
		path: '/drydock/royalchallengers',
		component: RoyalChallengersDryDock
	},
	{
		path: '/drydock/seahawks/sidequest',
		component: SidequestDrydock
	},
	{
		path: '/adjustmentmodule',
		component: adobeAnalytics.TrackPageView(
			AdjustmentLayout,
			pageViewArgs(pageNames.adjustmentLayout, siteSections.adjustmentLayout)
		)
	},
	{
		path: '/smartscreens',
		component: adobeAnalytics.TrackPageView(
			smartScreens,
			pageViewArgs(pageNames.smartScreens, siteSections.smartScreens)
		)
	},
	{
		path: '/smartscreens/sem',
		component: adobeAnalytics.TrackPageView(
			smartScreens,
			pageViewArgs(pageNames.smartScreensSem, siteSections.smartScreens)
		)
	},
	{
		path: '/smartscreens/440gl',
		component: adobeAnalytics.TrackPageView(
			smartScreens,
			pageViewArgs(pageNames.smartScreensSem, siteSections.smartScreens)
		)
	},
	{
		path: '/smartscreens/independence',
		component: adobeAnalytics.TrackPageView(
			smartScreens,
			pageViewArgs(pageNames.smartScreensIndependence, siteSections.smartScreens)
		)
	},
	{
		path: '/reviewnotes/:categoryId?/:noteId?/:entityid?',
		component: adobeAnalytics.TrackPageView(RNHParent, pageViewArgs(pageNames.reviewNotes, siteSections.reviewNotes))
	},
	{
		path: '/archivetimeline',
		component: adobeAnalytics.TrackPageView(
			ArcTimelineLayout,
			pageViewArgs(pageNames.archiveTimeline, siteSections.archiveTimeline)
		)
	},
	{
		path: '/dashboard/maps',
		component: adobeAnalytics.TrackPageView(DashboardV2, pageViewArgs(pageNames.maps, siteSections.dashboard, true))
	},
	{
		path: '/dashboard',
		component: adobeAnalytics.TrackPageView(
			DashboardV2,
			pageViewArgs(pageNames.engagementOverview, siteSections.dashboard, true)
		)
	},
	{
		path: '/manageteam',
		component: adobeAnalytics.TrackPageView(
			ManageTeamLayout,
			pageViewArgs(pageNames.manageTeam, siteSections.manageTeam)
		)
	},
	{
		path: '/copyhub',
		component: adobeAnalytics.TrackPageView(CopyHubLayout, pageViewArgs(pageNames.copyHub, siteSections.copyHub))
	},
	{
		path: '/copyhub/received',
		component: adobeAnalytics.TrackPageView(CopyHubLayout, pageViewArgs(pageNames.received, siteSections.copyHub))
	},
	{
		path: '/manageteam/external',
		component: adobeAnalytics.TrackPageView(ManageTeamLayout, pageViewArgs(pageNames.external, siteSections.manageTeam))
	},
	{
		path: '/contentupdates',
		component: adobeAnalytics.TrackPageView(
			ContentUpdateLayout,
			pageViewArgs(pageNames.contentUpdates, siteSections.contentUpdates)
		)
	},
	{
		path: '/contentupdates/forms',
		component: adobeAnalytics.TrackPageView(
			ContentUpdateLayout,
			pageViewArgs(pageNames.forms, siteSections.contentUpdates)
		)
	},
	{
		path: '/contentupdates/sync',
		component: adobeAnalytics.TrackPageView(
			ContentUpdateLayout,
			pageViewArgs(pageNames.sync, siteSections.contentUpdates)
		)
	},
	{
		path: '/contentupdates/pspaccounts',
		component: adobeAnalytics.TrackPageView(
			ContentUpdateLayout,
			pageViewArgs(pageNames.pspAccounts, siteSections.contentUpdates)
		)
	},
	{
		path: '/documents',
		component: DocumentListLayout
	},
	{
		path: '/documents/:documentid',
		component: adobeAnalytics.TrackPageView(DocumentOpen, pageViewArgs(pageNames.documentOpen, siteSections.documents))
	},
	{
		path: '/helixprojects',
		component: adobeAnalytics.TrackPageView(
			HelixProjectsLayout,
			pageViewArgs(pageNames.helixProjects, siteSections.helixProjects)
		)
	},
	{
		path: '/connections',
		component: adobeAnalytics.TrackPageView(
			HelixProjectsLayoutConnection,
			pageViewArgs(pageNames.connections, siteSections.connections)
		)
	},
	{
		path: '/connections/:engagementHelixId?',
		component: adobeAnalytics.TrackPageView(
			HelixProjectsLayoutConnection,
			pageViewArgs(pageNames.engagement, siteSections.connections)
		)
	},
	{
		path: '/financialstatements',
		component: adobeAnalytics.TrackPageView(
			FinancialStatements,
			pageViewArgs(pageNames.financialStatements, siteSections.financialStatements)
		)
	},
	{
		path: '/financialstatements/accountclass/:accountClassId?',
		component: adobeAnalytics.TrackPageView(ClassView, pageViewArgs(pageNames.class, siteSections.financialStatements))
	},
	{
		path: '/leadsheet/:accountId?',
		component: adobeAnalytics.TrackPageView(LeadSheetView, pageViewArgs(pageNames.account, siteSections.leadSheet))
	},
	{
		path: '/systemdiagnostics',
		component: adobeAnalytics.TrackPageView(
			SystemDiagnosticDocuments,
			pageViewArgs(pageNames.systemDiagnostics, siteSections.systemDiagnostics)
		)
	},
	{
		path: '/copyhub/createpackage',
		component: adobeAnalytics.TrackPageView(
			CreatePackageWizard,
			pageViewArgs(pageNames.createPackage, siteSections.copyHub)
		)
	},
	// TODO: Remove after All View Collaboration is fully tested
	{
		path: '/collaboration/requests/all/all',
		component: ({location}) => <Redirect to={{pathname: '/collaborationv2', search: location.search}} />
	},
	{
		path: '/collaboration/all/all/:id',
		component: ({match, location}) => (
			<Redirect to={{pathname: `/collaborationv2/${match.params.id}`, search: location.search}} />
		)
	},
	{
		path: '/collaboration/requests/:filtertypeId?/:quickfilterId?/:requestId?',
		component: ({location}) => <Redirect to={{pathname: '/collaborationv2', search: location.search}} />
	},
	{
		path: '/collaboration',
		component: ({location}) => <Redirect to={{pathname: '/collaborationv2', search: location.search}} />
	},
	{
		path: '/collaborationv2/:requestId?',
		component: adobeAnalytics.TrackPageView(
			CollaborationV2,
			pageViewArgs(pageNames.request, siteSections.collaboration)
		)
	},
	{
		path: '/collaborationv2',
		component: adobeAnalytics.TrackPageView(
			CollaborationV2,
			pageViewArgs(pageNames.requests, siteSections.collaboration)
		)
	},
	{
		path: '/activityfeed/:entityId?',
		component: adobeAnalytics.TrackPageView(
			ActivityFeedLayout,
			pageViewArgs(pageNames.activityFeed, siteSections.activityFeed)
		)
	},
	{
		path: '/groupsummary',
		component: adobeAnalytics.TrackPageView(
			GroupSummary,
			pageViewArgs(pageNames.groupSummary, siteSections.groupSummary)
		)
	},
	{
		path: '/receiveinstructions',
		component: adobeAnalytics.TrackPageView(
			ReceiveInstructions,
			pageViewArgs(pageNames.receiveInstructions, siteSections.receiveInstructions)
		)
	},
	// {
	// 	path: '/reporting',
	// 	component: Reporting
	// },
	{
		path: '/reports/:reportName?',
		component: adobeAnalytics.TrackPageView(Reports, pageViewArgs(pageNames.reports, siteSections.reports))
	},
	{
		path: '/reportgrid',
		component: adobeAnalytics.TrackPageView(ReportingGrid, pageViewArgs(pageNames.reportGrid, siteSections.reports))
	},
	{
		path: '/buildsteps',
		component: adobeAnalytics.TrackPageView(buildstep, pageViewArgs(pageNames.buildSteps, siteSections.buildSteps))
	},
	{
		path: '/locallandingpage',
		component: adobeAnalytics.TrackPageView(
			LocalLandingPageWrapper,
			pageViewArgs(pageNames.localLandingPage, siteSections.localLandingPage)
		)
	},
	{
		path: '/componentriskassessment/:GAComponentId',
		component: adobeAnalytics.TrackPageView(
			GroupInstructions,
			pageViewArgs(pageNames.groupInstructions, siteSections.groupInstructions)
		)
	},
	{
		path: '/cookiesettings',
		component: () =>
			adobeAnalytics.TrackPageView(
				<CookieSettings locale={labels.cookieSettings} />,
				pageViewArgs(pageNames.cookieSettings, siteSections.consent)
			)
	},
	{
		path: '', // default, goes to dashboard
		component: () => <Redirect to={{pathname: '/dashboard', search: location.search}} />
	}
];

if (env.allowDrydocks()) {
	console.log('Drydocks are allowed in this environment');
} else {
	routes = routes.filter((r) => !/drydock/i.test(r.path));
}

const App = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const isSuperModalOpen = useIsSuperModalOpen();

	let userPreferences = useSelector((state) => state.userPreferences);
	let isPaansPolicyAccepted = useSelector((state) => state.paansuserdata?.hasAccepted);
	const isModalOpen = useSelector((state) => state.isModalOpen);
	const isADCModalOpen = useSelector((state) => state.isADCModalOpen);
	const isGlobalErrorToastHidden = useSelector((state) => state.isGlobalErrorToastHidden);

	const isErrorToastDisplayed = !isGlobalErrorToastHidden && !isModalOpen && !isADCModalOpen;
	const queryParameters = queryString.parse(location.search.toLowerCase());
	const isDrawerMode = queryParameters?.isdrawer && queryParameters.isdrawer.toLowerCase() === 'true';
	const engagementId = useEngagementId();
	const displayCookieConsent = useSelector((state) => state.adobeAnalytics.promptForConsent);

	//Drawer related
	const frameService = useFrameService();
	const subscriptionRef = useRef(null);
	const currentRef = useRef(location);

	const updateFrameTitle = useCallback(({name, deepLinkSrc, frameKey}) => {
		if (frameKey) {
			frameService.updateFrame(frameKey, {
				name,
				deepLinkSrc
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//Collapase drawer when we navigate to different routes
	useEffect(() => {
		if (location.pathname !== currentRef.current?.pathname) {
			toggleFrameModal({frameArgs: {event: frameEvents.TOGGLE_DRAWER, open: false}});
			currentRef.current = location;
		} else if (location.search !== currentRef.current?.search) {
			toggleFrameModal({frameArgs: {event: frameEvents.TOGGLE_DRAWER, open: false}});
			currentRef.current = location;
		}
	}, [location]);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.CLOSE_MENU) {
							window.dispatchEvent(new CustomEvent('closeMenu'));
						}
						if (args.subEvent === frameSubEvent.UPDATE_FRAME_TITLE && args.updateFrameTitle) {
							updateFrameTitle(args);
						}
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	const framesLabels = useMemo(() => {
		return {
			maxTabsLocked: labels.maxTabsLocked,
			openInNewTab: labels.openInNewTab,
			close: labels.close,
			open: labels.expand,
			copy: labels.copyLink,
			copySuccess: labels.copySuccess,
			copyFailure: labels.copyFailure,
			minimize: labels.collapse
		};
	}, [labels]);

	const onCoookieConsentResult = (type) => {
		dispatch(adobeAnalytics.setConsentResult(type));
	};

	useEffect(() => {
		let appInsights = getAppInsights();

		if (appInsights && userPreferences.emailAddress) {
			console.log('Setting userId in Application Insights');
			appInsights.setAuthenticatedUserContext(userPreferences.emailAddress);
		}
	}, [userPreferences]);

	useEffect(() => {
		if (isDrawerMode && engagementId > 0) {
			dispatch(getQuickLinks(env.getBaseURL(serviceURL, Number(engagementId)) + 'QuickLinks')).catch((result) => {
				if (result && result.response && result.response.status === 403) {
					dispatch(raiseJSValidationError(labels.invalidEngagementId));
				}
			});
		}
	}, [isDrawerMode, engagementId]);

	// Adobe Analytics
	useEffect(() => {
		if (adobeAnalyticsEnabled && userPreferences && userPreferences.emailAddress) {
			dispatch(
				adobeAnalytics.initAdobeAnalytics(
					userPreferences.emailAddress,
					!userPreferences.IsGuestUser,
					envConfig.adobeAnalytics.defaultDataLayer
				)
			);
		}
	}, [userPreferences]);

	return (
		<>
			<Main>
				<Suspense fallback={<LoadingIndicator styleName={'SuspenseLoadingIndicator'} fullscreen />}>
					{!Utility.pageInIframe() && <ReactiveFrame labels={framesLabels} />}
					{isErrorToastDisplayed && <ToastMessageContainer className="app-messages-container" />}
					<Switch>
						{userPreferences.isPreferencesLoaded && isPaansPolicyAccepted ? (
							routes.map((eachRoute) => {
								const {path, component} = eachRoute;
								return <Route exact path={path} component={component} />;
							})
						) : (
							<LoaderContainer path={location.pathname} />
						)}
					</Switch>
				</Suspense>
			</Main>
			{displayCookieConsent && `${location.pathname}${location.search}` !== COOKIE_SETTINGS_PATH && (
				<CookieConsentModal
					settingsURL={COOKIE_SETTINGS_PATH}
					onResult={onCoookieConsentResult}
					appName={labels.versionlabel}
					labels={{
						title: labels.aaCookieConsentTitle,
						prompt: labels.aaCookieContentPrompt,
						explanationHTML: labels.aaCookieConsentExplanation,
						explanationHTMLWithDoNotTrack: labels.aaCookieConsentExplanationWithDoNotTrack,
						declineOptionalAction: labels.aaCookieConsentDeclineOptionalAction,
						acceptAllAction: labels.aaCookieConsentAcceptAllAction,
						customizeAction: labels.aaCookieConsentCustomizeAction,
						customizeURL: labels.aaCookieConsentCustomizeURL
					}}
				/>
			)}
		</>
	);
};

export default hot(module)(withRouter(App));
